import React from "react";
import classnames from "classnames";
import { SeatAddonInfo } from "../../../hooks/useSeatAddonInfo";

interface SeatAddonInlineProps {
    seatInfo: SeatAddonInfo;
    checked: boolean;
    onToggle: (checked: boolean) => void;
    isAnnual: boolean;
}

export function SeatAddonInline({ seatInfo, checked, onToggle, isAnnual }: SeatAddonInlineProps) {
    const {
        hasSeatAddon,
        extraSeatsNeeded,
        defaultSeatsToAdd,
        exceedsMax,
        maxAddonSeats,
        pricePerSeat,
        totalOccupants,
        baseSeats,
        isLoading
    } = seatInfo;

    if (isLoading || extraSeatsNeeded <= 0) {
        return null;
    }

    const seatPrice = isAnnual ? pricePerSeat * 12 : pricePerSeat;
    const interval = isAnnual ? "/yr" : "/mo";

    if (!hasSeatAddon) {
        return <NonAddonPlanWarning totalOccupants={totalOccupants} baseSeats={baseSeats} />;
    }

    if (exceedsMax) {
        return (
            <ExceedsMaxWarning
                maxAddonSeats={maxAddonSeats}
                baseSeats={baseSeats}
                totalOccupants={totalOccupants}
                seatPrice={seatPrice}
                interval={interval}
            />
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <label className="flex cursor-pointer items-start gap-2">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onToggle(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand"
                />
                <div className="flex flex-col gap-1 text-sm">
                    <span className="font-medium text-gray-900">
                        Add {defaultSeatsToAdd} extra {defaultSeatsToAdd === 1 ? "seat" : "seats"}{" "}
                        <span className="font-normal text-gray-500">
                            ({defaultSeatsToAdd} x ${seatPrice}
                            {interval} per seat)
                        </span>
                    </span>
                    <span className="text-xs text-gray-400">
                        Your workspace has {totalOccupants}{" "}
                        {totalOccupants === 1 ? "member" : "members"}, this plan includes{" "}
                        {baseSeats}
                    </span>
                </div>
            </label>
            {!checked && (
                <div className="mt-2 flex items-start gap-1.5 rounded-md bg-yellow-50 p-2 text-xs text-yellow-800">
                    <i className="far fa-exclamation-triangle mt-0.5 text-yellow-600"></i>
                    <span>
                        You&apos;ll need to remove {extraSeatsNeeded}{" "}
                        {extraSeatsNeeded === 1 ? "member" : "members"} after upgrading to stay
                        within your seat limit.
                    </span>
                </div>
            )}
        </div>
    );
}

function NonAddonPlanWarning({
    totalOccupants,
    baseSeats
}: {
    totalOccupants: number;
    baseSeats: number;
}) {
    const excess = totalOccupants - baseSeats;
    return (
        <div className="flex items-start gap-1.5 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-800">
            <i className="far fa-exclamation-triangle mt-0.5 text-yellow-600"></i>
            <div className="flex flex-col gap-1">
                <span className="font-medium">
                    Your workspace has {totalOccupants}{" "}
                    {totalOccupants === 1 ? "member" : "members"} but this plan only includes{" "}
                    {baseSeats} {baseSeats === 1 ? "seat" : "seats"}.
                </span>
                <span>
                    You&apos;ll need to remove {excess} {excess === 1 ? "member" : "members"} or{" "}
                    <a
                        href="https://roboflow.com/sales"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-yellow-900 underline"
                    >
                        talk to Sales
                    </a>{" "}
                    for a plan that fits your team.
                </span>
            </div>
        </div>
    );
}

function ExceedsMaxWarning({
    maxAddonSeats,
    baseSeats,
    totalOccupants,
    seatPrice,
    interval
}: {
    maxAddonSeats: number;
    baseSeats: number;
    totalOccupants: number;
    seatPrice: number;
    interval: string;
}) {
    return (
        <div className="flex flex-col gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-800">
            <div className="flex items-start gap-1.5">
                <i className="far fa-exclamation-triangle mt-0.5 text-yellow-600"></i>
                <div className="flex flex-col gap-1">
                    <span className="font-medium">
                        Your workspace has {totalOccupants}{" "}
                        {totalOccupants === 1 ? "member" : "members"} but this plan supports up to{" "}
                        {baseSeats + maxAddonSeats} seats ({baseSeats} included + {maxAddonSeats}{" "}
                        add-on at ${seatPrice}
                        {interval} each).
                    </span>
                    <span>
                        <a
                            href="https://roboflow.com/sales"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-yellow-900 underline"
                        >
                            Talk to Sales
                        </a>{" "}
                        for a plan that fits your team.
                    </span>
                </div>
            </div>
        </div>
    );
}
